import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import {
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Nav,
  Tab,
  Table,
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  getIntegrationDetailAPI,
  getTenantsListAPI,
  resetTokenAPI,
  runConnectivityTestAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import { platformUrl } from '../../../utils/mhNav';
import styles from './IntegrationDetail.module.scss';

const VALID = new Set(['microsoft', 'google', 'whatsapp', 'openai']);

export default function IntegrationDetail() {
  const { provider } = useParams();
  const [tenantOpts, setTenantOpts] = useState([]);
  const [tenantSel, setTenantSel] = useState(null);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testMsg, setTestMsg] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [reconnectOpen, setReconnectOpen] = useState(false);
  const [payloadModal, setPayloadModal] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTenantsLoading(true);
      try {
        const res = await getTenantsListAPI({ limit: 100, page: 1 });
        if (cancelled) return;
        const opts = (res.data.items || []).map((t) => ({
          value: t.id,
          label: `${t.tenantCode} — ${t.companyName}`,
        }));
        setTenantOpts(opts);
        setTenantSel((prev) => prev || opts[0] || null);
      } catch (e) {
        if (!cancelled) toast.error(e.message);
      } finally {
        if (!cancelled) setTenantsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadDetail = useCallback(async () => {
    if (!VALID.has(provider) || !tenantSel?.value) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setError(null);
    setTestMsg(null);
    try {
      const res = await getIntegrationDetailAPI(tenantSel.value, provider);
      setDetail(res.data);
    } catch (e) {
      setError(e);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [provider, tenantSel]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const jobColumns = useMemo(
    () => [
      { name: 'Job ID', selector: (r) => r.id, sortable: true },
      { name: 'Type', selector: (r) => r.type, sortable: true },
      {
        name: 'Created',
        selector: (r) => new Date(r.createdAt).toLocaleString(),
        sortable: true,
      },
      { name: 'Status', selector: (r) => r.status, sortable: true },
      { name: 'Duration (ms)', selector: (r) => r.durationMs, sortable: true },
    ],
    []
  );

  if (!VALID.has(provider)) {
    return (
      <div>
        <PageHeader
          title="Integration"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Integrations', path: platformUrl('integrations') },
            { label: 'Unknown' },
          ]}
        />
        <EmptyState icon={AlertTriangle} title="Unknown provider" description="Use Microsoft, Google, WhatsApp, or OpenAI." />
      </div>
    );
  }

  if (tenantsLoading) {
    return (
      <div>
        <PageHeader
          title={`${provider} integration`}
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Integrations', path: platformUrl('integrations') },
            { label: provider },
          ]}
        />
        <LoadingSkeleton rows={4} cols={2} />
      </div>
    );
  }

  const s = detail?.summary;

  return (
    <div>
      <PageHeader
        title={`${provider} integration`}
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Integrations', path: platformUrl('integrations') },
          { label: provider },
        ]}
        secondaryActions={[
          {
            label: 'Reconnect',
            variant: 'outline-primary',
            onClick: () => setReconnectOpen(true),
          },
          {
            label: 'View logs',
            variant: 'outline-secondary',
            onClick: () => {
              window.location.assign(
                `${platformUrl('jobs')}?tenant=${encodeURIComponent(tenantSel?.value || '')}&provider=${encodeURIComponent(provider)}`
              );
            },
          },
        ]}
      />

      <Card className="mb-3">
        <Card.Body>
          <Form.Label>Tenant</Form.Label>
          <Select
            options={tenantOpts}
            value={tenantSel}
            onChange={setTenantSel}
            isSearchable
            placeholder="Search tenants…"
          />
        </Card.Body>
      </Card>

      {error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load integration"
          description={error.message}
          actionLabel="Retry"
          onAction={loadDetail}
        />
      )}

      {loading && <LoadingSkeleton rows={4} cols={2} />}

      {!loading && !error && detail && (
        <>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge bg={s?.tokenStatus === 'valid' ? 'success' : 'danger'}>Token: {s?.tokenStatus}</Badge>
            <Badge bg="secondary">
              Scopes: {s?.scopesOk}/{s?.scopesTotal}
            </Badge>
            <Badge bg={s?.webhookStatus === 'active' ? 'success' : 'warning'}>Webhook: {s?.webhookStatus}</Badge>
            <Badge bg="light" text="dark">
              Last success: {s?.lastSuccessAt ? new Date(s.lastSuccessAt).toLocaleString() : '—'}
            </Badge>
            <Badge bg="light" text="dark">
              Last failure: {s?.lastFailureAt ? new Date(s.lastFailureAt).toLocaleString() : '—'}
            </Badge>
          </div>

          <Tab.Container defaultActiveKey="auth">
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="auth">Auth</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="jobs">Jobs</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="payloads">Payloads</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="settings">Settings</Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="auth">
                <Card>
                  <Card.Body>
                    <p>
                      Token {s?.tokenStatus === 'valid' ? 'valid' : 'expired'} — expires{' '}
                      {s?.tokenExpiresAt ? new Date(s.tokenExpiresAt).toLocaleString() : '—'}
                    </p>
                    <Table size="sm" responsive bordered>
                      <tbody>
                        {(s?.scopes || []).map((sc) => (
                          <tr key={sc.name}>
                            <td>{sc.name}</td>
                            <td>{sc.ok ? '✓' : '✗'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {testMsg && (
                      <div
                        className={`alert ${testMsg.ok ? 'alert-success' : 'alert-danger'} py-2 small`}
                        role="status"
                      >
                        {testMsg.text}
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            const res = await runConnectivityTestAPI(tenantSel.value, provider);
                            setTestMsg({
                              ok: res.data.ok,
                              text: res.data.message,
                            });
                            toast[res.data.ok ? 'success' : 'error'](res.data.message);
                          } catch (e) {
                            toast.error(e.message);
                          }
                        }}
                      >
                        Run test
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => setResetOpen(true)}>
                        Reset token
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Tab.Pane>
              <Tab.Pane eventKey="jobs">
                <DataTableWrapper
                  columns={jobColumns}
                  data={detail?.jobs || []}
                  loading={false}
                  keyField="id"
                  showSearch={false}
                />
              </Tab.Pane>
              <Tab.Pane eventKey="payloads">
                <ul className="list-unstyled small">
                  {(detail?.payloads || []).map((p) => (
                    <li key={p.id} className="border-bottom py-2">
                      <Button variant="link" className="p-0 me-2" onClick={() => setPayloadModal(p)}>
                        {new Date(p.receivedAt).toLocaleString()}
                      </Button>
                      <Badge bg={p.signatureValid ? 'success' : 'danger'}>sig {p.signatureValid ? 'ok' : 'bad'}</Badge>
                    </li>
                  ))}
                </ul>
              </Tab.Pane>
              <Tab.Pane eventKey="settings">
                <Card>
                  <Card.Body>
                    <Table size="sm">
                      <tbody>
                        <tr>
                          <td>Tenant ID</td>
                          <td>{detail?.settings?.tenantAppId}</td>
                        </tr>
                        <tr>
                          <td>Client ID</td>
                          <td>{detail?.settings?.clientIdMasked}</td>
                        </tr>
                        <tr>
                          <td>Webhook URL</td>
                          <td className="text-break">{detail?.settings?.webhookUrl}</td>
                        </tr>
                        <tr>
                          <td>Region</td>
                          <td>{detail?.settings?.region}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </>
      )}

      {!loading && !error && !detail && tenantSel && (
        <EmptyState icon={AlertTriangle} title="No data" description="Could not load details for this tenant." />
      )}

      <ConfirmModal
        isOpen={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={async () => {
          try {
            await resetTokenAPI(tenantSel.value, provider);
            toast.success('Token reset (simulated)');
            setResetOpen(false);
            loadDetail();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Reset token?"
        message="Connected flows may fail until the tenant reconnects."
        confirmLabel="Reset"
        danger
      />

      <ConfirmModal
        isOpen={reconnectOpen}
        onClose={() => setReconnectOpen(false)}
        onConfirm={() => {
          toast.success('Reconnect flow started (simulated)');
          setReconnectOpen(false);
        }}
        title="Reconnect integration?"
        message="This will start the OAuth reconnect wizard for the tenant."
        confirmLabel="Reconnect"
      />

      <Modal show={Boolean(payloadModal)} onHide={() => setPayloadModal(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Webhook payload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between mb-2">
            <Button size="sm" variant="outline-secondary" onClick={() => setShowSecrets((v) => !v)}>
              {showSecrets ? 'Hide secrets' : 'Show'}
            </Button>
          </div>
          <pre className={styles.jsonBlock}>
            {payloadModal ? (showSecrets ? payloadModal.bodyFull : payloadModal.bodyPreview) : ''}
          </pre>
        </Modal.Body>
      </Modal>
    </div>
  );
}
